import {
  Autocomplete,
  TextField,
  Box,
  InputAdornment,
  Typography,
  Popper,
  Button,
} from "@mui/material";
import "./Card.css";
import SearchIcon from "@mui/icons-material/Search";
import { WebsitePermissions } from "../../../../data/user/WebsitePermissions";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { WebsiteWithUuid } from "../../../../data/website/Website";
import { Stack } from "@mui/system";
import {
  setSelectedDate,
  setSelectedOptionGroup,
  setSelectedOptionStatus,
  setSelectedSite,
  setSitePages,
} from "../../../../features/assetManagementSlice";
import store from "../../../../store";
import { useDispatch } from "react-redux";

export interface CardProps {
  selectedSiteName: string | undefined;
  setSelectedSiteName: (name: string) => void;
  userWebsitePermissions: WebsitePermissions;
}

interface Website {
  site_name: string;
  site_url: string;
}

interface WebsitePermission {
  [key: string]: Website;
}
export default function Card({
  selectedSiteName,
  setSelectedSiteName,
  userWebsitePermissions,
}: CardProps) {
  const [selectedSiteTemp, setSelectedSiteTemp] =
    useState<WebsitePermission | null>(null);
  const dispatch = useDispatch();
  const location = useLocation(); // Hook to get the current location
  const [searchParams, setSearchParams] = useSearchParams();

  const CustomPopper = (props: any) => {
    return (
      <Popper
        {...props}
        modifiers={[{ name: "flip", enabled: false }]}
        placement="bottom-start"
      />
    );
  };

  const selectedSite = useMemo(() => {
    return (
      userWebsitePermissions.find(
        (website) => Object.keys(website)[0] === selectedSiteName
      ) ?? null
    );
    // eslint-disable-next-line
  }, [selectedSiteName, searchParams]);

  const [value, setValue] = useState(selectedSite) as any;
  useEffect(() => {
    if (location.pathname === "/assets/page_management") {
      setValue(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const selectedSiteFromParams = searchParams.get("selectedSite");
    if (selectedSiteFromParams) {
      const matchingSite = userWebsitePermissions.find(
        (website) => Object.keys(website)[0] === selectedSiteFromParams
      );
      if (matchingSite) {
        setValue(matchingSite);
        setSelectedSiteTemp(matchingSite);
        store.dispatch(setSelectedSite(selectedSiteFromParams));
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("selectedPage");
        setSearchParams(newParams, { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const handleSelectSite = (
    _: unknown,
    selectedPermission: WebsiteWithUuid
  ) => {
    if (selectedPermission) {
      setSelectedSiteTemp(selectedPermission); // Store selection temporarily
      setValue(selectedPermission);
      store.dispatch(setSelectedSite(Object.keys(selectedPermission)[0]));
    }
  };
  const handleApplySelection = () => {
    if (selectedSiteTemp) {
      const siteName = Object.keys(selectedSiteTemp)[0];
      setSelectedSiteName?.(siteName);

      // Preserve existing search params while updating selectedSite
      const newParams = new URLSearchParams(searchParams);
      newParams.set("selectedSite", siteName);
      setSearchParams(newParams);
      dispatch(setSelectedOptionStatus([]));
      dispatch(setSelectedOptionGroup([]));
      dispatch(setSelectedDate([]));
      newParams.delete("filters");
      newParams.delete("pageTitle");
      setSearchParams(newParams);
    }
  };

  return (
    <div className="card5">
      <div className="paper32">
        <div className="card-elements23">
          <div className="cardmedia23">
            <img
              className="library-placeholder-image23"
              alt=""
              src="/-library--placeholder-image.svg"
            />
          </div>
          <div className="cardheader-parent4">
            <div className="cardheader34">
              <div className="content55">
                <p className="header43">PAGE BUILDER - ASSET SELECTION</p>
                <div className="subheader47">{`{Subheader}`}</div>
              </div>
            </div>
          </div>
          <div className="cardcontent-parent9">
            <div className="cardcontent45">
              <Stack direction="row" gap={4} sx={{ width: "100%" }}>
                <Autocomplete
                  sx={{ width: "100%" }}
                  disablePortal
                  id="combo-box-demo"
                  value={value}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleSelectSite(event, newValue);
                    } else {
                      // Clear selection
                      setSelectedSiteTemp(null);
                      setValue(null);
                    }
                    dispatch(setSitePages([]));
                  }}
                  onInputChange={(_, __, reason) => {
                    if (reason === "clear") {
                      setValue(null); // Reset to default when the clear button is clicked
                      setSelectedSiteName(""); // Reset selected site
                    }
                  }}
                  noOptionsText={
                    <Typography variant="body1">
                      No matches were found
                    </Typography>
                  }
                  getOptionLabel={(option: any) =>
                    (Object.values(option)[0] as any)?.ui_site_name?.toString()
                  }
                  options={userWebsitePermissions as any}
                  renderOption={(propsOptions, option: any) => (
                    <Box
                      component="li"
                      {...propsOptions}
                      key={(Object.values(option)[0] as any)?.ui_site_name}
                    >
                      {(Object.values(option)[0] as any)?.ui_site_name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search site name or choose from the list"
                      label="Select Website"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ marginLeft: "12px" }} />
                          </InputAdornment>
                        ),
                      }}
                      color="secondary"
                    />
                  )}
                  size="small"
                  PopperComponent={CustomPopper}
                />

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleApplySelection}
                  disabled={!selectedSiteTemp}
                  sx={{ minWidth: "120px" }}
                  data-tracker="3"
                >
                  SELECT SITE
                </Button>
              </Stack>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
